import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn, SlideIn, StaggerContainer, StaggerItem } from '../components/layout';
import { Clock, CreditCard, Users, ChevronRight } from 'lucide-react';
import ReviewsCarousel from '../components/ReviewsCarousel';
import heroImage from '../assets/images/institut-dsrs1.png';
import homeImage from '../assets/images/accueil1.png';
import homebloc1 from '../assets/images/home-bloc1.png';
import homebloc2 from '../assets/images/home-bloc2.png';
import homebloc3 from '../assets/images/home-bloc3.png';


const services = [
  {
    title: 'Onglerie & Pédicure',
    description: 'Pose, dépose, remplissage, soins des ongles et pédicure complète.',
    link: '/onglerie-pedicure',
    image: homebloc1
  },
  {
    title: 'Bien-être',
    description: 'Massages Madero, drainage lymphatique, massage californien.',
    link: '/bien-etre',
    image: homebloc2
  },
  {
    title: 'Press On Nails',
    description: 'Faux ongles personnalisés sur mesure, à emporter chez vous.',
    link: '/press-on-nails',
    image: homebloc3
  }
];

export default function HomePage() {
  return (
    <PageTransition>
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${heroImage})`,
            filter: 'brightness(0.4)'
          }}
        />
        
        {/* Decorative gradients */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--rose-gold)] rounded-full opacity-20 blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--gold)] rounded-full opacity-15 blur-[100px]" />

        {/* Content */}
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xs uppercase tracking-[0.4em] text-[var(--rose-gold)] mb-6"
          >
            Institut Confidentiel
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-['Cormorant_Garamond'] text-5xl md:text-7xl lg:text-8xl font-light mb-6 leading-[0.9]"
          >
            Madame DSRS
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-lg md:text-xl font-light text-white/80 mb-10 max-w-2xl mx-auto"
          >
            Onglerie · Pédicure · Bien-être
            <br />
            <span className="text-sm text-white/60">Une expérience beauté exclusive et personnalisée</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="https://www.planity.com/madame-dsrs-95570-attainville"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-planity"
              className="px-10 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
            >
              Prendre Rendez-vous
            </a>
            <Link
              to="/onglerie-pedicure"
              data-testid="hero-cta-discover"
              className="px-10 py-4 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/10 transition-colors duration-300"
            >
              Découvrir nos prestations
            </Link>
            <Link
              to="/carte-cadeau"
              data-testid="hero-cta-giftcard"
              className="px-10 py-4 border border-[var(--rose-gold)] text-[var(--rose-gold)] text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--rose-gold)]/10 transition-colors duration-300"
            >
              Offrez une carte cadeau
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-white py-6 border-b border-[var(--champagne)]" data-testid="quick-info-section">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-wrap justify-center gap-8 md:gap-16">
          <div className="flex items-center gap-3 text-[var(--charcoal)]">
            <Clock size={20} className="text-[var(--gold)]" />
            <span className="text-sm">Sur rendez-vous uniquement</span>
          </div>
          <div className="flex items-center gap-3 text-[var(--charcoal)]">
            <Users size={20} className="text-[var(--gold)]" />
            <span className="text-sm">Accompagnant non accepté</span>
          </div>
          <div className="flex items-center gap-3 text-[var(--charcoal)]">
            <CreditCard size={20} className="text-[var(--gold)]" />
            <span className="text-sm">CB · Espèces · Acompte 30%</span>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 px-6" data-testid="about-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <SlideIn direction="left">
            <div className="relative">
              <img
                src={homeImage}
                alt="Institut Madame DSRS"
                className="w-full h-[500px] object-cover grayscale-[20%]"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-[var(--gold)] -z-10" />
            </div>
          </SlideIn>
          
          <SlideIn direction="right">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
                À propos
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-6 text-[var(--charcoal)]">
                Votre Parenthèse Beauté Privée
              </h2>
              <p className="text-[var(--charcoal)]/70 mb-6 leading-relaxed">
                Entrez dans un univers dédié à la mise en beauté et au lâcher-prise. 
                Passionnée et attentive, je vous propose des prestations personnalisées, pensées pour sublimer chaque détail.
              </p>
              <p className="text-[var(--charcoal)]/70 mb-8 leading-relaxed">
                L’institut vous reçoit sur rendez-vous uniquement, dans un cadre intime et élégant, afin de vous offrir une expérience unique, douce et entièrement centrée sur vous.
              </p>
              <Link
                to="/onglerie-pedicure"
                data-testid="about-cta"
                className="inline-flex items-center gap-2 text-[var(--dark-rosegold)] text-sm uppercase tracking-[0.15em] font-medium hover:text-[var(--gold)] transition-colors"
              >
                Découvrir les prestations
                <ChevronRight size={16} />
              </Link>
            </div>
          </SlideIn>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white/50" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <FadeIn className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
              Nos Prestations
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[var(--charcoal)]">
              Des Soins Signature
            </h2>
          </FadeIn>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <StaggerItem key={service.title}>
                <Link
                  to={service.link}
                  data-testid={`service-card-${index}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden mb-6">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl mb-2 text-[var(--charcoal)] group-hover:text-[var(--dark-rosegold)] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[var(--charcoal)]/60">
                    {service.description}
                  </p>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[var(--charcoal)] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[var(--gold)] rounded-full opacity-10 blur-[80px]" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[var(--rose-gold)] rounded-full opacity-10 blur-[80px]" />
        
        <div className="max-w-[800px] mx-auto px-6 text-center relative z-10">
          <FadeIn>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-white mb-6">
              Prêt(e) à vivre l'expérience DSRS ?
            </h2>
            <p className="text-white/70 mb-8">
              Réservez votre créneau en ligne et offrez-vous un moment de beauté et de détente.
            </p>
            <a
              href="https://www.planity.com/madame-dsrs-95570-attainville"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-planity"
              className="inline-block px-10 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
            >
              Réserver maintenant
            </a>
          </FadeIn>
        </div>
      </section>

      {/* Testimonials Section - Dynamic Carousel */}
      <section className="py-24 md:py-32 px-6" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto">
          <FadeIn className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
              Témoignages
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[var(--charcoal)]">
              Ce que disent nos clientes
            </h2>
          </FadeIn>

          <div className="px-6 md:px-12">
            <ReviewsCarousel />
          </div>
        </div>
      </section>

      {/* Important Info Section */}
      <section className="py-24 bg-gradient-to-br from-[var(--champagne)]/30 to-white" data-testid="info-section">
        <div className="max-w-[1000px] mx-auto px-6">
          <FadeIn className="text-center mb-12">
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[var(--charcoal)]">
              Préparer votre visite
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FadeIn delay={0.1}>
              <div className="bg-white p-8 border border-[var(--champagne)]">
                <h4 className="font-['Cormorant_Garamond'] text-xl mb-3 text-[var(--charcoal)]">
                  Institut Confidentiel
                </h4>
                <p className="text-sm text-[var(--charcoal)]/70">
                  L'adresse exacte vous sera communiquée après confirmation de votre rendez-vous.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="bg-white p-8 border border-[var(--champagne)]">
                <h4 className="font-['Cormorant_Garamond'] text-xl mb-3 text-[var(--charcoal)]">
                  Accompagnant
                </h4>
                <p className="text-sm text-[var(--charcoal)]/70">
                  Pour préserver l'intimité des soins, les accompagnants ne sont pas acceptés.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="bg-white p-8 border border-[var(--champagne)]">
                <h4 className="font-['Cormorant_Garamond'] text-xl mb-3 text-[var(--charcoal)]">
                  Moyens de paiement
                </h4>
                <p className="text-sm text-[var(--charcoal)]/70">
                  Carte bancaire et espèces acceptées. Acompte de 30% requis à la réservation.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.4}>
              <div className="bg-white p-8 border border-[var(--champagne)]">
                <h4 className="font-['Cormorant_Garamond'] text-xl mb-3 text-[var(--charcoal)]">
                  Annulation
                </h4>
                <p className="text-sm text-[var(--charcoal)]/70">
                  Annulation gratuite jusqu'à 48h avant le rendez-vous. Au-delà, l'acompte sera conservé.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
